/**
 * Categorical color tokens — the single source of truth for "what hue does this
 * category member get". Issue #336: tier, status, and component-type colors had
 * drifted across the app (e.g. the Professional tier was blue in one table and
 * purple in another). Components assign a hue here once; badge *shape* (radius,
 * padding, border vs soft fill) stays a per-component concern.
 *
 * Tailwind only keeps classes that appear as complete literal strings, so the
 * hue → class maps below are spelled out in full rather than interpolated.
 */

export type CategoricalHue =
  'sky' | 'blue' | 'indigo' | 'purple' | 'green' | 'amber' | 'orange' | 'red' | 'zinc';

/** Soft filled badge (bg-100 / text-800) — used by tables, chips, status pills. */
const SOFT_BADGE: Record<CategoricalHue, string> = {
  sky: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  zinc: 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-300',
};

/** Bordered tint badge (bg-100 / text-700 / border-300) — used by SubscriptionBadge. */
const TINT_BADGE: Record<CategoricalHue, string> = {
  sky: 'bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700',
  blue: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
  indigo:
    'bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700',
  purple:
    'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
  green:
    'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
  amber:
    'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  orange:
    'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700',
  red: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
  zinc: 'bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700',
};

/** Plan-tier hue assignment. Tiers must stay visually distinct from one another. */
export const TIER_HUE: Record<string, CategoricalHue> = {
  free: 'zinc',
  starter: 'blue',
  professional: 'purple',
  business: 'green',
  partner: 'amber',
  enterprise: 'indigo',
};

/** Subscription/billing status hue. Red = problem, amber = attention, green = good. */
export const SUBSCRIPTION_STATUS_HUE: Record<string, CategoricalHue> = {
  active: 'green',
  trialing: 'blue',
  past_due: 'amber',
  paused: 'zinc',
  canceled: 'red',
  cancelled: 'red',
  incomplete: 'orange',
  incomplete_expired: 'orange',
};

export function softBadgeClass(hue: CategoricalHue): string {
  return SOFT_BADGE[hue];
}

export function tintBadgeClass(hue: CategoricalHue): string {
  return TINT_BADGE[hue];
}

export function tierHue(slug: string | null | undefined): CategoricalHue {
  return (slug && TIER_HUE[slug.toLowerCase()]) || 'zinc';
}

export function tierBadgeClass(slug: string | null | undefined): string {
  return SOFT_BADGE[tierHue(slug)];
}

export function subscriptionStatusBadgeClass(status: string | null | undefined): string {
  return SOFT_BADGE[(status && SUBSCRIPTION_STATUS_HUE[status.toLowerCase()]) || 'zinc'];
}

/** Health/severity status hue. Green = healthy, amber = attention, red = error. */
export const HEALTH_STATUS_HUE: Record<string, CategoricalHue> = {
  healthy: 'green',
  needs_attention: 'amber',
  missing_data: 'zinc',
  error: 'red',
};

export function healthStatusBadgeClass(status: string | null | undefined): string {
  return SOFT_BADGE[(status && HEALTH_STATUS_HUE[status.toLowerCase()]) || 'zinc'];
}

/** Stripe webhook processing status hue. Green = processed, red = failed. */
export const WEBHOOK_STATUS_HUE: Record<string, CategoricalHue> = {
  processed: 'green',
  failed: 'red',
};

export function webhookStatusBadgeClass(status: string | null | undefined): string {
  return SOFT_BADGE[(status && WEBHOOK_STATUS_HUE[status.toLowerCase()]) || 'zinc'];
}

/** Deployment/execution status hue. Sky = in-progress, green = done, red = failed. */
export const DEPLOYMENT_STATUS_HUE: Record<string, CategoricalHue> = {
  pending: 'zinc',
  running: 'sky',
  paused_rebooting: 'amber',
  paused_blocked: 'amber',
  completed: 'green',
  success: 'green',
  failed: 'red',
  error: 'red',
  cancelled: 'zinc',
};

export function deploymentStatusBadgeClass(status: string | null | undefined): string {
  return SOFT_BADGE[(status && DEPLOYMENT_STATUS_HUE[status.toLowerCase()]) || 'zinc'];
}

/**
 * Color portion of a component-type badge. `componentDisplay.ts` assigns each
 * component type a `badgeColor`; this resolves it to one categorical hue so the
 * three builders (ExpandableSequenceStep, SequenceBuilder, TemplatePicker) stop
 * each carrying their own copy of the switch.
 */
export function componentBadgeColorClass(badgeColor: string): string {
  const known: CategoricalHue[] = [
    'blue',
    'green',
    'purple',
    'amber',
    'indigo',
    'sky',
    'orange',
    'red',
  ];
  return SOFT_BADGE[
    known.includes(badgeColor as CategoricalHue) ? (badgeColor as CategoricalHue) : 'zinc'
  ];
}
